import axios from 'axios';

const API_URL = process.env.REACT_APP_ENV === 'local' ? `http://localhost:${process.env.REACT_APP_API_PORT || '3000'}/api` : '/api';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies in requests
});

// Add auth token to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  requestMagicLink: (email, options = {}) =>
    api.post('/auth/request-magic-link', {
      email,
      ...options,
    }),
  verifyMagicLink: (token) => api.get(`/auth/verify/${token}`),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  // Link a Sleeper username to the signed-in account (always on, not feature-flagged)
  linkSleeper: (sleeperUsername) => api.post('/auth/link-sleeper', { sleeperUsername }),
};

// Sleeper API
export const sleeperAPI = {
  getUser: (username) => api.get(`/sleeper/user/${username}`),
  // forSleeperUserId triggers backend self-heal after orphan takeover —
  // forces a Sleeper refresh if the cached rosters don't include the user.
  getLeague: (leagueId, forSleeperUserId) =>
    api.get(`/sleeper/league/${leagueId}`, {
      params: forSleeperUserId ? { forSleeperUserId } : undefined,
    }),
  refreshLeague: (leagueId) => api.post(`/sleeper/league/${leagueId}/refresh`),
};

// Trade Packages API
export const tradePackagesAPI = {
  create: (packageData) => api.post('/trade-packages', packageData),
  list: (status) => api.get('/trade-packages', { params: { status } }),
  myTrades: () => api.get('/trade-packages/my-trades'),
  getByLink: (uniqueLink) => api.get(`/trade-packages/${uniqueLink}`),
  getRosters: (uniqueLink) => api.get(`/trade-packages/${uniqueLink}/rosters`),
  grade: (uniqueLink, gradeData) => api.post(`/trade-packages/${uniqueLink}/grade`, gradeData),
  counterPropose: (uniqueLink, proposalData) => api.post(`/trade-packages/${uniqueLink}/counter-propose`, proposalData),
  delete: (packageId) => api.delete(`/trade-packages/${packageId}`),
};

// Players API
export const playersAPI = {
  getBatch: (playerIds) => api.post('/players/batch', { playerIds }),
};

// Tweets API
export const tweetsAPI = {
  forPlayers: (playerIds, limit = 100) =>
    api.get('/tweets/for-players', {
      params: { playerIds: playerIds.join(','), limit },
    }),
  hideGlobally: (tweetId) => api.post(`/tweets/${tweetId}/hide`),
  pollNow: () => api.post('/tweets/poll-now'),
};

// Manager Intel API
export const managerIntelAPI = {
  getProfile: (sleeperUsername, viewerUsername) =>
    api.get(`/manager-intel/${sleeperUsername}`, {
      params: { viewer: viewerUsername },
    }),
  getDossier: (sleeperUsername) =>
    api.get(`/manager-intel/${sleeperUsername}/profile`),
  getTrades: (sleeperUsername, filters) =>
    api.get(`/manager-intel/${sleeperUsername}/trades`, {
      params: filters, // { filter, myRosterPlayerIds, theirRosterPlayerIds }
    }),
  getPartnerTrades: (partnerSleeperUsername, viewerSleeperUsername) =>
    api.get(`/manager-intel/${partnerSleeperUsername}/partner-trades`, {
      params: { viewer: viewerSleeperUsername },
    }),
  // Cross-user trade comps for the canvas "Anyone · this trade" filter.
  // playerIds and pickKeys are arrays; pickKey shape is "<season>:<round>".
  getTradeComps: (playerIds = [], pickKeys = [], limit = 50) =>
    api.get('/manager-intel/trade-comps', {
      params: {
        playerIds: (playerIds || []).join(','),
        pickKeys: (pickKeys || []).join(','),
        limit,
      },
    }),
  getNotes: (sleeperUsername) =>
    api.get(`/manager-intel/${sleeperUsername}/notes`),
  saveNotes: (sleeperUsername, notes) =>
    api.post(`/manager-intel/${sleeperUsername}/notes`, { notes }),
  getSyncStatus: (sleeperUsername) =>
    api.get(`/manager-intel/sync/status/${sleeperUsername}`),
  triggerSync: (sleeperUsername, priorityLeagueId, forceRefresh = false) =>
    api.post('/manager-intel/sync/trigger', {
      sleeperUsername,
      priorityLeagueId,
      forceRefresh,
    }),
  getRecentManagers: (limit = 12) =>
    api.get('/manager-intel/recent', {
      params: { limit },
    }),
  getDynastyYoY: (sleeperUsername) =>
    api.get(`/manager-intel/${sleeperUsername}/dynasty-yoy`),
  getDynastyYoYStatus: (sleeperUsername) =>
    api.get(`/manager-intel/${sleeperUsername}/dynasty-yoy-status`),
  refreshDynastyYoY: (sleeperUsername) =>
    api.post(`/manager-intel/${sleeperUsername}/dynasty/refresh`),
};

// Orphan Marketplace API
export const orphanMarketplaceAPI = {
  list: (params) => api.get('/orphan-marketplace', { params }),
  getById: (id) => api.get(`/orphan-marketplace/${id}`),
  create: (data) => api.post('/orphan-marketplace', data),
  update: (id, data) => api.patch(`/orphan-marketplace/${id}`, data),
  apply: (id, data) => api.post(`/orphan-marketplace/${id}/apply`, data),
  getApplications: (id) => api.get(`/orphan-marketplace/${id}/applications`),
  rejectApplication: (orphanId, applicationId, declineNote) => api.post(`/orphan-marketplace/${orphanId}/applications/${applicationId}/reject`, { declineNote }),
  markAdopted: (id, data) => api.post(`/orphan-marketplace/${id}/adopt`, data),
  withdraw: (id) => api.delete(`/orphan-marketplace/${id}`),
  getMyListings: () => api.get('/orphan-marketplace/user/my-listings'),
  getMyApplications: () => api.get('/orphan-marketplace/user/my-applications'),
};

// Startup Marketplace API
export const startupMarketplaceAPI = {
  list: (params) => api.get('/startup-marketplace', { params }),
  getById: (id) => api.get(`/startup-marketplace/${id}`),
  create: (data) => api.post('/startup-marketplace', data),
  update: (id, data) => api.patch(`/startup-marketplace/${id}`, data),
  apply: (id, data) => api.post(`/startup-marketplace/${id}/apply`, data),
  getApplications: (id) => api.get(`/startup-marketplace/${id}/applications`),
  acceptApplication: (listingId, applicationId, data = {}) => api.post(`/startup-marketplace/${listingId}/applications/${applicationId}/accept`, data),
  rejectApplication: (listingId, applicationId, declineNote) => api.post(`/startup-marketplace/${listingId}/applications/${applicationId}/reject`, { declineNote }),
  markJoined: (listingId, applicationId) => api.post(`/startup-marketplace/${listingId}/applications/${applicationId}/mark-joined`),
  withdraw: (id) => api.delete(`/startup-marketplace/${id}`),
  getMyListings: () => api.get('/startup-marketplace/user/my-listings'),
  getMyApplications: () => api.get('/startup-marketplace/user/my-applications'),
};

// Admin API
export const adminAPI = {
  getMetrics: () => api.get('/admin/metrics'),
  getTimeSeries: (days = 30) => api.get('/admin/metrics/timeseries', { params: { days } }),

  // Queue operations
  clearFailedJobs: () => api.post('/admin/queue/clear-failed'),
  clearCompletedJobs: () => api.post('/admin/queue/clear-completed'),
  retryFailedJobs: () => api.post('/admin/queue/retry-failed'),
  flushQueue: () => api.post('/admin/queue/flush'),
  getFailedJobs: (limit = 20) => api.get('/admin/queue/failed-jobs', { params: { limit } }),
  getQueueStatus: () => api.get('/admin/queue/status'),
  deleteJob: (jobId) => api.delete(`/admin/queue/job/${jobId}`),

  // Script operations
  updateKTC: () => api.post('/admin/scripts/update-ktc'),
  updateSleeperPlayers: () => api.post('/admin/scripts/update-sleeper-players'),

  // Error management
  clearAllErrors: () => api.post('/admin/errors/clear-all'),
  clearError: (username) => api.post(`/admin/errors/clear/${username}`),

  // Scheduled job error management
  clearAllScheduledJobErrors: () => api.post('/admin/scheduled-jobs/errors/clear-all'),
  clearScheduledJobError: (errorId) => api.post(`/admin/scheduled-jobs/errors/clear/${errorId}`),

  // Analytics
  getAnalyticsOverview: () => api.get('/admin/analytics/overview'),
  getPopularPages: (days = 7) => api.get('/admin/analytics/popular-pages', { params: { days } }),
  getRecentActivity: (params = {}) => api.get('/admin/analytics/recent-activity', { params }),
  getAnalyticsTimeSeries: (days = 14) => api.get('/admin/analytics/timeseries', { params: { days } }),

  // Engagement (Simulations + Manager Intel focus)
  getSimulationStats: (granularity = 'day') =>
    api.get('/admin/engagement/simulations', { params: { granularity } }),
  getManagerIntelEngagement: (granularity = 'day') =>
    api.get('/admin/engagement/manager-intel', { params: { granularity } }),
  getFeatureOverview: () => api.get('/admin/engagement/feature-overview'),

  // Analyst review oversight — read-only, one analyst per call (by slug)
  getAnalystReviews: (slug) => api.get(`/admin/analysts/${slug}/reviews`),
  getAnalystReviewDetail: (slug, reviewId) => api.get(`/admin/analysts/${slug}/reviews/${reviewId}`),
};

// Rate My Trade API
export const rateMyTradeAPI = {
  list: (params) => api.get('/rate-my-trade', { params }),
  getUserLeagues: (username) => api.get(`/rate-my-trade/user/${username}/leagues`),
  getLeagueRosters: (leagueId) => api.get(`/rate-my-trade/leagues/${leagueId}/rosters`),
  getLeagueTransactions: (leagueId, userId) => api.get(`/rate-my-trade/leagues/${leagueId}/transactions`, { params: { userId } }),
  create: (data) => api.post('/rate-my-trade', data),
  getByShareId: (shareId) => api.get(`/rate-my-trade/${shareId}`),
  vote: (shareId, vote) => api.post(`/rate-my-trade/${shareId}/vote`, { vote }),
  mine: () => api.get('/rate-my-trade/mine'),
  claim: (data) => api.post('/rate-my-trade/claim', data),
};

// Analyst Reviews API
export const analystAPI = {
  listAnalysts: () => api.get('/analysts'),
  getAnalyst: (slug) => api.get(`/analysts/${slug}`),
  linkSleeper: (sleeperUsername) => api.post('/analyst-reviews/link-sleeper', { sleeperUsername }),
  // items: [{ type, tradeId?, leagueId?, payload?, question?, context? }]
  // productType: 'trade_review' (default) | 'roster_review' (one item, type 'roster', leagueId required)
  initiateReview: ({ items, analystId, productType }) =>
    api.post('/analyst-reviews', { items, analystId, ...(productType ? { productType } : {}) }),
  confirmPayment: (reviewId, paymentIntentId) =>
    api.post(`/analyst-reviews/${reviewId}/confirm`, { paymentIntentId }),
  getReview: (reviewId) => api.get(`/analyst-reviews/${reviewId}`),
  // Analyst dashboard
  getQueue: () => api.get('/analyst/queue'),
  getReviewDetail: (reviewId) => api.get(`/analyst/reviews/${reviewId}`),
  // responses: [{ id, analystResponse }], grades: { tradeValue, tradeValueSide, timelineFit, capitalImpact, overallGrade }
  submitReview: (reviewId, responses, grades) => api.post(`/analyst/reviews/${reviewId}/submit`, { responses, grades }),
  // Stripe Connect
  getStripeStatus: () => api.get('/analyst/stripe/status'),
  startStripeOnboarding: () => api.post('/analyst/stripe/onboard'),
};

// Monte Carlo API
export const monteCarloAPI = {
  getBootstrap: (leagueId) => api.get(`/monte-carlo/league/${leagueId}/bootstrap`),
  saveSimulation: (data) => api.post('/monte-carlo/simulations', data),
  listSimulations: () => api.get('/monte-carlo/simulations'),
  getSimulation: (id) => api.get(`/monte-carlo/simulations/${id}`),
  deleteSimulation: (id) => api.delete(`/monte-carlo/simulations/${id}`),
};

export default api;
